import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { AdminPage } from '@/components/Admin'
import { AlertBanner } from '@/components/AlertBanner'
import { TenantIdentityCard } from '@/components/TenantIdentity'
import { updateTenantIdentidade } from '@/api/tenantApi'
import { useTenant } from '@/contexts/TenantContext'
import { useSaveFeedback } from '@/hooks/useSaveFeedback'
import { getTenantSlug } from '@/config/tenant'
import adminStyles from './Admin.module.css'
import styles from './AdminIdentidadePage.module.css'

const CONCEPTS = [
  {
    icon: '▣',
    title: 'Empresa (conta)',
    text: 'O seu negócio no sistema — slug e nome. Dados isolados: clientes, agendamentos e conteúdo.',
  },
  {
    icon: '◉',
    title: 'Cliente',
    text: 'Pessoa que usa a app e marca horários. Pertence sempre à sua empresa.',
  },
  {
    icon: '◎',
    title: 'Unidade / loja',
    text: 'Filial ou morada para mapa e agendamentos, dentro da mesma conta.',
    link: '/admin/unidades',
    linkLabel: 'Gerir unidades',
  },
  {
    icon: '◇',
    title: 'Logo da app',
    text: 'Imagem que os clientes veem no menu e no login da app.',
    link: '/admin/menu',
    linkLabel: 'Menu / Banner',
  },
] as const

export function AdminIdentidadePage() {
  const { nome, logoUrl, refresh, setCachedNome } = useTenant()
  const [editNome, setEditNome] = useState(nome)

  useEffect(() => {
    setEditNome(nome)
  }, [nome])

  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null)
  const { notifySaved, notifyError } = useSaveFeedback()

  const slug = getTenantSlug()
  const hasChanges = editNome.trim() !== nome.trim()
  const charCount = editNome.trim().length

  async function handleSave(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    setMsg(null)
    try {
      const updated = await updateTenantIdentidade(editNome)
      setCachedNome(updated.nome)
      await refresh()
      const okText = 'Nome da empresa atualizado.'
      setMsg({ type: 'ok', text: okText })
      notifySaved(okText)
    } catch (err) {
      notifyError(err, 'Erro ao guardar.')
      setMsg({
        type: 'err',
        text: err instanceof Error ? err.message : 'Erro ao guardar.',
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <AdminPage
      title="Identidade da empresa"
      subtitle="Como a sua marca aparece para clientes, profissionais e no painel admin."
      wide
    >
      {msg && (
        <AlertBanner
          variant={msg.type === 'ok' ? 'success' : 'error'}
          message={msg.text}
          onDismiss={() => setMsg(null)}
        />
      )}

      <div className={styles.layout}>
        <div className={styles.mainCol}>
          <section className={styles.hero}>
            <div className={styles.heroText}>
              <span className={styles.heroEyebrow}>A sua marca</span>
              <p className={styles.heroNome}>{editNome.trim() || nome}</p>
              <div className={styles.heroMeta}>
                <code className={styles.slugChip}>{slug}</code>
                {logoUrl ? (
                  <span className={styles.logoBadge}>Logo configurado</span>
                ) : (
                  <span className={styles.logoBadgeMuted}>Sem logo — configure no menu</span>
                )}
              </div>
            </div>
          </section>

          <section className={styles.previewCard} aria-labelledby="id-preview-title">
            <header className={styles.cardHead}>
              <span className={styles.cardIcon} aria-hidden>
                ◈
              </span>
              <div>
                <h2 id="id-preview-title" className={styles.cardTitle}>
                  Pré-visualização
                </h2>
                <p className={styles.cardSub}>Como a identidade aparece no painel e na app</p>
              </div>
            </header>

            <div className={styles.previewContexts}>
              <div className={styles.previewContext}>
                <span className={styles.previewContextLabel}>Painel admin</span>
                <div className={styles.previewFrame}>
                  <TenantIdentityCard showSlug nomeOverride={editNome} />
                </div>
              </div>
              <div className={styles.previewContext}>
                <span className={styles.previewContextLabel}>App do cliente</span>
                <div className={`${styles.previewFrame} ${styles.previewFrameClient}`}>
                  <div className={styles.clientMock}>
                    <div className={styles.clientMockBar} />
                    <div className={styles.clientMockBody}>
                      <TenantIdentityCard compact nomeOverride={editNome} />
                      <div className={styles.clientMockLines} aria-hidden>
                        <span />
                        <span />
                        <span />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <form className={styles.formCard} onSubmit={handleSave}>
            <header className={styles.cardHead}>
              <span className={styles.cardIcon} aria-hidden>
                ✎
              </span>
              <div>
                <h2 className={styles.cardTitle}>Nome de exibição</h2>
                <p className={styles.cardSub}>
                  Login dos clientes, menu da app e cabeçalho deste painel
                </p>
              </div>
            </header>

            <p className={styles.help}>
              O identificador técnico <code>{slug}</code> não muda aqui — foi definido na criação da conta.
            </p>

            <div className={`${adminStyles.formGroup} ${styles.field}`}>
              <label htmlFor="id-nome">Nome da empresa</label>
              <input
                id="id-nome"
                type="text"
                value={editNome}
                onChange={(e) => setEditNome(e.target.value)}
                maxLength={120}
                required
                autoComplete="organization"
                placeholder="Ex.: Salão Bela Vista"
              />
              <div className={styles.fieldFooter}>
                <span className={styles.charCount}>{charCount} / 120</span>
                {hasChanges && <span className={styles.unsavedHint}>Alterações por guardar</span>}
              </div>
            </div>

            <div className={styles.formActions}>
              <button
                type="submit"
                className={adminStyles.btnSalvar}
                disabled={saving || !hasChanges}
              >
                {saving ? 'A guardar…' : 'Guardar nome'}
              </button>
              {hasChanges && (
                <button
                  type="button"
                  className={adminStyles.btnSecondary}
                  onClick={() => setEditNome(nome)}
                  disabled={saving}
                >
                  Desfazer
                </button>
              )}
            </div>
          </form>
        </div>

        <aside className={styles.sideCol} aria-label="Guia da plataforma">
          <h2 className={styles.sideTitle}>Como funciona</h2>
          <p className={styles.sideLead}>
            Termos usados no sistema para organizar o seu negócio e os seus clientes.
          </p>
          <ul className={styles.conceptList}>
            {CONCEPTS.map((item) => (
              <li key={item.title} className={styles.conceptCard}>
                <span className={styles.conceptIcon} aria-hidden>
                  {item.icon}
                </span>
                <div className={styles.conceptBody}>
                  <strong className={styles.conceptTitle}>{item.title}</strong>
                  <p className={styles.conceptText}>{item.text}</p>
                  {'link' in item && item.link && (
                    <Link className={styles.conceptLink} to={item.link}>
                      {item.linkLabel} →
                    </Link>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </aside>
      </div>
    </AdminPage>
  )
}
